import type {ContentFile} from '../../../packages/content-client/src'
import type {ExportedSnapshot,PreparedSave,SaveLocation} from './suiteSave'

export interface RecoveryAccount {tenant_id:string;actor_id:string}
export interface RecoveryDraft extends ExportedSnapshot {
 id:string;scope:string;app:string;name:string;base:ContentFile|null
 generation:number;updatedAt:number;intent:PreparedSave|null
 location?:SaveLocation
}
export type RecoverySummary=Omit<RecoveryDraft,'bytes'|'intent'>&{pendingSave:boolean}
const DATABASE='folio-private-recovery',STORE='drafts',META='summaries',MAX_BYTES=10*1024*1024,MAX_TOTAL=100*1024*1024,MAX_DRAFTS=50,RETENTION=14*86400000
export const recoveryScope=(account:RecoveryAccount)=>JSON.stringify([account.tenant_id,account.actor_id])
export const recoveryAccount=(value:unknown):value is RecoveryAccount=>!!value&&typeof (value as RecoveryAccount).tenant_id==='string'&&typeof (value as RecoveryAccount).actor_id==='string'
export const draftLink=(draft:Pick<RecoveryDraft,'id'|'app'|'base'>)=>'/apps/'+draft.app+'?'+new URLSearchParams({draft:draft.id,...(draft.base?{file:draft.base.file_id}:{})})
function database():Promise<IDBDatabase>{return new Promise((resolve,reject)=>{
 const request=indexedDB.open(DATABASE,1)
 request.onupgradeneeded=()=>{request.result.createObjectStore(STORE,{keyPath:'id'});const summaries=request.result.createObjectStore(META,{keyPath:'id'});summaries.createIndex('scope','scope')}
 request.onsuccess=()=>resolve(request.result)
 request.onerror=()=>reject(Error('Private recovery storage is unavailable. Download a copy to keep your work.'))
 request.onblocked=()=>reject(Error('Close older Folio tabs to enable private recovery.'))
})}
function done(transaction:IDBTransaction):Promise<void>{return new Promise((resolve,reject)=>{
 transaction.oncomplete=()=>resolve();transaction.onerror=transaction.onabort=()=>reject(transaction.error||Error('The private checkpoint could not be saved.'))
})}
function result<T>(request:IDBRequest<T>):Promise<T>{return new Promise((resolve,reject)=>{request.onsuccess=()=>resolve(request.result);request.onerror=()=>reject(request.error)})}
/** Browser-local bytes are scoped to a currently authenticated stable IAM identity.
 * Source rights must be checked again before exposing/restoring any file draft.
 * Checkpoints never publish, mark the engine saved, or replace canonical revisions.
 */
export async function readDraft(account:RecoveryAccount,id:string):Promise<RecoveryDraft|null>{
 const db=await database()
 try{const row=await result<RecoveryDraft|undefined>(db.transaction(STORE).objectStore(STORE).get(id));return row?.scope===recoveryScope(account)&&row.updatedAt>Date.now()-RETENTION?row:null}finally{db.close()}
}
export async function listDrafts(account:RecoveryAccount):Promise<RecoverySummary[]>{
 const db=await database()
 try{
  const rows=await result<RecoverySummary[]>(db.transaction(META).objectStore(META).index('scope').getAll(recoveryScope(account)))
  return rows.filter(row=>row.updatedAt>Date.now()-RETENTION).sort((a,b)=>b.updatedAt-a.updatedAt)
 }finally{db.close()}
}
export async function checkpoint(draft:Omit<RecoveryDraft,'generation'|'updatedAt'>,expected:number):Promise<RecoveryDraft>{
 if(!(draft.bytes instanceof ArrayBuffer)||!draft.bytes.byteLength||draft.bytes.byteLength>MAX_BYTES)throw Error('Private checkpoints are limited to 10 MiB.')
 const db=await database()
 try{
  // One read/write transaction serializes tabs. A stale writer forks a draft
  // instead of replacing another tab's more recent accepted checkpoint.
  const tx=db.transaction([STORE,META],'readwrite'),finished=done(tx),store=tx.objectStore(STORE),summaries=tx.objectStore(META)
  const rows=await result<(RecoverySummary&{size:number})[]>(summaries.getAll()),previous=rows.find(row=>row.id===draft.id)
  const collision=previous&&(previous.scope!==draft.scope||previous.generation!==expected)
  const id=collision?crypto.randomUUID():draft.id
  let count=0,total=0
  for(const row of rows){
   if(row.updatedAt<Date.now()-RETENTION){store.delete(row.id);summaries.delete(row.id);continue}
   if(row.id!==id){count++;total+=row.size}
  }
  if(count>=MAX_DRAFTS||total+draft.bytes.byteLength+(draft.intent?.bytes.byteLength||0)>MAX_TOTAL){
   tx.abort();await finished.catch(()=>{});throw Error('Private recovery storage is full. Save or download your existing drafts before continuing.')
  }
  const saved={...draft,id,generation:collision?1:(previous?.generation||0)+1,updatedAt:Date.now()}
  const {bytes,intent,...metadata}=saved
  store.put(saved);summaries.put({...metadata,pendingSave:!!intent,size:bytes.byteLength+(intent?.bytes.byteLength||0)});await finished;return saved
 }finally{db.close()}
}
export async function removeDraft(account:RecoveryAccount,id:string,generation:number):Promise<boolean>{
 const db=await database()
 try{
  const tx=db.transaction([STORE,META],'readwrite'),finished=done(tx),store=tx.objectStore(STORE)
  const row=await result<RecoveryDraft|undefined>(store.get(id))
  const matches=row?.scope===recoveryScope(account)&&row.generation===generation
  if(matches){store.delete(id);tx.objectStore(META).delete(id)}
  await finished;return !!matches
 }finally{db.close()}
}

/** Serializes exact intent/checkpoint writes and removal, including overlapping
 * exports and saves. Each tab starts a separate journal; stale copies fork.
 */
export class RecoveryJournal {
 record:RecoveryDraft|null=null
 private tail:Promise<unknown>=Promise.resolve()
 constructor(readonly account:RecoveryAccount,readonly app:string,public id:string,private changed:(record:RecoveryDraft|null)=>void){}
 async load(){const row=await readDraft(this.account,this.id);this.record=row?.app===this.app?row:null;return this.record}
 private enqueue<T>(task:()=>Promise<T>):Promise<T>{const next=this.tail.then(task);this.tail=next.catch(()=>{});return next}
 save(snapshot:ExportedSnapshot,name:string,base:ContentFile|null,intent:PreparedSave|null,location?:SaveLocation){return this.enqueue(async()=>{
  const row=await checkpoint({...snapshot,id:this.id,scope:recoveryScope(this.account),app:this.app,name,base,intent,location},this.record?.generation||0)
  this.id=row.id;this.record=row;this.changed(row);return row
 })}
 clear(){return this.enqueue(async()=>{if(this.record)await removeDraft(this.account,this.id,this.record.generation);this.record=null;this.changed(null)})}
 settled(){return this.tail}
}
